//Data used to be in model => move to config file (dimensions.json)
//Set config parameter to trigger that code
//Define the structure of the json
/*
{
"modelSetup": {
    "units": "mm",
    "Screw Head Type": "Bulge Head"
  },
  "Screw Gauge": "15",
  "Screw Length": "12"
}

Lookup table:
Bugle, Countersunk, Winged: Full
Trim, Hex, Button, Round, Pan, Wafer: partial
Raised: Nearly Full => treat like partial
 */

//Extract first word and use switch function to use either bounding box (full) or length (partial)
//Pass gauge & screw length
import React, { useEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { DimensionLine } from "../components/DimensionLine";
import { DimensionLabel } from "../components/DimensionLabel";
import { Arrowhead } from "../components/ArrowHead";
import { log, error, warn, info, debug } from '../../../utils/consoleLogger';

const SimpleCoordinates = ({
    configJson,
    box,
    gltfScene,
    params,
    isCameraSettled,
    showLabels,
    line_width,
    isMobile,
    sceneBackgroundColour,
    setScaledArrowHeightOut,
    scaledArrowHeight,
    minArrowHeight,
    distanceScale,
    dimensionsOnTop,
    numDigits
}) => {

    const [depthLabel, setDepthLabel] = useState("");
    const [depthStart, setDepthStart] = useState(new THREE.Vector3());
    const [depthEnd, setDepthEnd] = useState(new THREE.Vector3());
    const [depthOffset, setDepthOffset] = useState(new THREE.Vector3());
    const [smallDimDepthLabel, setSmallDimDepthLabel] = useState("");
    const [smallDimDepthStart, setSmallDimDepthStart] = useState(new THREE.Vector3());
    const [smallDimDepthEnd, setSmallDimDepthEnd] = useState(new THREE.Vector3());
    const [smallDimDepthOffset, setSmallDimDepthOffset] = useState(new THREE.Vector3());
    const [widthLabel, setWidthLabel] = useState("");
    const [widthStart, setWidthStart] = useState(new THREE.Vector3());
    const [widthEnd, setWidthEnd] = useState(new THREE.Vector3());
    const [widthOffset, setWidthOffset] = useState(new THREE.Vector3());
    const [heightLabel, setHeightLabel] = useState("");
    const [heightStart, setHeightStart] = useState(new THREE.Vector3());
    const [heightEnd, setHeightEnd] = useState(new THREE.Vector3());
    const [heightOffset, setHeightOffset] = useState(new THREE.Vector3());
    const [heightHeadLabel, setHeightHeadLabel] = useState("");
    const [heightHeadStart, setHeightHeadStart] = useState(new THREE.Vector3());
    const [heightHeadEnd, setHeightHeadEnd] = useState(new THREE.Vector3());
    const [heightHeadOffset, setHeightHeadOffset] = useState(new THREE.Vector3());
    const [lengthShaftLabel, setLengthShaftLabel] = useState("");
    const [lengthShaftStart, setLengthShaftStart] = useState(new THREE.Vector3());
    const [lengthShaftEnd, setLengthShaftEnd] = useState(new THREE.Vector3());
    const [lengthShaftOffset, setLengthShaftOffset] = useState(new THREE.Vector3());
    const [lengthThreadLabel, setLengthThreadLabel] = useState("");
    const [lengthThreadStart, setLengthThreadStart] = useState(new THREE.Vector3());
    const [lengthThreadEnd, setLengthThreadEnd] = useState(new THREE.Vector3());
    const [lengthThreadOffset, setLengthThreadOffset] = useState(new THREE.Vector3());
    const [diameterShaftLabel, setDiameterShaftLabel] = useState("");
    const [diameterShaftStart, setDiameterShaftStart] = useState(new THREE.Vector3());
    const [diameterShaftEnd, setDiameterShaftEnd] = useState(new THREE.Vector3());
    const [diameterShaftOffset, setDiameterShaftOffset] = useState(new THREE.Vector3());
    const [fullLengthDimension, setFullLengthDimension] = useState(false);
    const [dimensions, setDimensions] = useState();
    const dimensionsMapRef = useRef(new Map());
    const previousDimensionsRef = useRef(null);
    const [scaleFactor, setScaleFactor] = useState(1000);
    const [unit, setUnit] = useState("mm");
    const line_model_offset = 0.02;
    const line_floor_offset = 0.005;
    const arrow_head_offset = 0.01;
    const lineOffset = 1;
    const labelOffset = 0.1;
    const minLabelOffset = 3;
    

    const maxAxisIndex = (box) => {
        const boxSize = new THREE.Vector3();
        box.getSize(boxSize);
        return boxSize.x >= boxSize.y && boxSize.x >= boxSize.z
            ? 0
            : boxSize.y >= boxSize.z
                ? 1
                : 2;
    }

    const minAxisIndex = (box) => {
        const boxSize = new THREE.Vector3();
        box.getSize(boxSize);
        return boxSize.x <= boxSize.y && boxSize.x <= boxSize.z
          ? 0
          : boxSize.y <= boxSize.z
            ? 1
            : 2;
      };

    useEffect(() => {

        if (!box || !gltfScene) return;

        const dimensionsMap = dimensionsMapRef.current;

        //deserialise json into productSettings here
        const productSettings = typeof configJson === "string" ? JSON.parse(configJson) : configJson;
        log('product settings:', productSettings);

        // Clear the map before populating it
        dimensionsMap.clear();

        const myUnit = productSettings.modelSetup?.units || "mm";
        setUnit(myUnit);
        const headType = (productSettings.modelSetup?.["Screw Head Type"] || "Bugle Head").split(' ')[0].toLowerCase();

        switch (headType) {
            case "trim":
            case "hex":
            case "button":
            case "round": 
            case "pan":
            case "wafer":
            case "raised":
            setFullLengthDimension(false);
            break;
            case "bugle":
            case "bulge":
            case "countersunk":
            case "winged":
            default:
            setFullLengthDimension(true);
            break;
        }
        const unitMultiplier = myUnit === "mm" ? 0.001 : unit === "cm" ? 0.01 : 1;
        setScaleFactor(myUnit === "mm" ? 1000 : myUnit === "cm" ? 100 : 1);

        // Keys we're searching for in the json
        const requiredKeys = ["Screw Gauge", "Screw Length"];

        let lengthValue = parseFloat(productSettings["Screw Length"]) || 0;
        if (!lengthValue || lengthValue == "") { setFullLengthDimension(true); }

        requiredKeys.forEach((key) => {
            if (key in productSettings) {
            const rawValue = productSettings[key];

            if (key === "Screw Gauge") {
                // Gauge has no position
                dimensionsMap.set('gauge', {
                value: `${rawValue}G`,
                position: null,
                });
            } else if (key === "Screw Length") {
                const numericValue = parseFloat(rawValue) || 0;
                dimensionsMap.set('length', {
                value: `${numericValue}${unit}`,
                position: new THREE.Vector3(0, numericValue * unitMultiplier, 0),
                });
            }
            }
        });

        log("Updated dimensionsMap:", dimensionsMap);

        const size = box.getSize(new THREE.Vector3());
        log('box size, scale factor:', size, myUnit === "mm" ? 1000 : unit === "cm" ? 100 : 1);

        const center = box.getCenter(new THREE.Vector3());
        log('dimensions map length:', dimensionsMap.get('length'), dimensionsMap.get('length').position, parseFloat(dimensionsMap.get('length').value)/ 1000, maxAxisIndex(box));
        const positions = {
            frontBottomRight1: new THREE.Vector3(
                box.max.x,
                box.min.y,
                box.max.z
            ),
            frontBottomRight2: new THREE.Vector3(
                box.max.x,
                box.min.y,
                box.max.z
            ),
            frontBottomRight3: new THREE.Vector3(
                box.max.x * 1 / 3,
                box.min.y,
                box.max.z * 1 / 3
            ),
            frontBottomRight4: new THREE.Vector3(
                box.max.x * 2 / 3,
                box.min.y,
                box.max.z * 2 / 3
            ),
            frontMiddleBottomDiameter: new THREE.Vector3(
                box.max.x,
                (box.min.y + 2 * box.max.y) / 3 - line_floor_offset,
                box.max.z
            ),
            backBottomRight1: new THREE.Vector3(
                box.max.x,
                box.min.y,
                box.min.z
            ),
            backBottomRight2: new THREE.Vector3(
                box.max.x,
                box.min.y,
                box.min.z
            ),
            backBottomRight3: new THREE.Vector3(
                box.max.x * 3,
                box.min.y,
                box.min.z * 3
            ),
            backBottomRight4: new THREE.Vector3(
                box.max.x * 1 / 3 + line_model_offset,
                box.min.y + line_floor_offset,
                box.min.z * 1 / 3
            ),
            backBottomRight5: new THREE.Vector3(
                box.max.x * 2 / 3 + line_model_offset,
                box.min.y + line_floor_offset,
                box.min.z * 2 / 3
            ),
            middleDiameter: new THREE.Vector3(
                center.x,
                (box.min.y + 2 * box.max.y) / 3,
                center.z
            ),
            frontBottomLeft1: new THREE.Vector3(
                box.min.x,
                box.min.y,
                box.max.z
            ),
            frontBottomLeft2: new THREE.Vector3(
                box.min.x,
                box.min.y,
                box.max.z
            ),
            frontBottomLeft3: new THREE.Vector3(
                box.min.x * 2 / 3 - line_model_offset,
                box.min.y + line_floor_offset,
                box.max.z * 2 / 3
            ),
            frontBottomLeft4: new THREE.Vector3(
                box.min.x * 1 / 3 - line_model_offset,
                box.min.y + line_floor_offset,
                box.max.z * 1 / 3
            ),
            frontMiddleBottomLeft: new THREE.Vector3(
                box.min.x * 2 / 3 + line_model_offset,
                (box.min.y + box.max.y) / 2 + line_floor_offset,
                box.max.z * 2 / 3
            ),
            backBottomLeft1: new THREE.Vector3(
                box.min.x,
                box.min.y,
                box.min.z
            ),
            backBottomLeft2: new THREE.Vector3(
                box.min.x,
                box.min.y,
                box.min.z
            ),
            backBottomLeft3: new THREE.Vector3(
                box.min.x,
                box.min.y,
                box.min.z
            ),
            backBottomLeftShaft: new THREE.Vector3(
                box.min.x * 2,
                box.min.y,
                box.min.z * 2
            ),
            backBottomLeftHead:
                // dimensionsMap.get('height_head') ? new THREE.Vector3(
                //     box.min.x * 2,
                //     (box.max.y) - dimensionsMap.get('height_head').value / 100,
                //     box.min.z * 2
                // ) : 
                undefined,
            backBottomLeftThread: new THREE.Vector3(
                box.min.x,
                box.min.y,
                box.min.z
            ),
            backMiddleBottomLeft: new THREE.Vector3(
                box.min.x * 2 / 3 + line_model_offset,
                (box.min.y + box.max.y) / 2 + line_floor_offset,
                box.min.z * 2 / 3
            ),
            topRight: new THREE.Vector3(
                box.max.x,
                box.max.y,
                box.min.z
            ),
            topRight2: new THREE.Vector3(
                (box.max.x * 1 / 3) + ((maxAxisIndex(box) === 0) ? dimensionsMap.get('length').value / scaleFactor : 0) + (line_model_offset),
                (box.min.y) + ((maxAxisIndex(box) === 1) ? dimensionsMap.get('length').value / scaleFactor : 0) - (line_model_offset),
                (box.min.z * 1 / 3) + ((maxAxisIndex(box) === 2) ? dimensionsMap.get('length').value / scaleFactor : 0)
            ),
            topRight3:
                // dimensionsMap.get('height_head') ? new THREE.Vector3(
                //     (box.max.x * 2 / 3) + line_model_offset,
                //     (box.max.y) - dimensionsMap.get('height_head').value / 100 - line_model_offset,
                //     (box.min.z * 2 / 3)
                // ) : 
                undefined,
            topLeft: new THREE.Vector3(
                box.min.x,
                box.max.y,
                box.min.z
            ),
            topLeftThread: new THREE.Vector3(
                box.min.x + ((maxAxisIndex(box) === 0) ? parseFloat(dimensionsMap.get('length').value) / scaleFactor : 0),
                (box.min.y) + ((maxAxisIndex(box) === 1) ? parseFloat(dimensionsMap.get('length').value) / scaleFactor  : 0),
                box.min.z + ((maxAxisIndex(box) === 2) ? parseFloat(dimensionsMap.get('length').value) / scaleFactor : 0)
            ),
            topLeftShaft:
                // dimensionsMap.get('length') ? new THREE.Vector3(
                //     box.min.x * 2 + ((maxAxisIndex(box) === 0) ? dimensionsMap.get('length').value / 100 : 0),
                //     box.min.y + ((maxAxisIndex(box) === 1) ? dimensionsMap.get('length').value / 100 : 0),
                //     box.min.z * 2 + ((maxAxisIndex(box) === 2) ? dimensionsMap.get('length').value / 100 : 0)
                // ) : 
                undefined,
            topLeftHead:
                // dimensionsMap.get('height_head') ? new THREE.Vector3(
                //     box.min.x * 2,
                //     box.max.y,
                //     box.min.z * 2
                // ) : 
                undefined,
        };

        log('Positions:', positions);
        setDimensions({ size, positions, center });

    }, [box, gltfScene]);

    const updateValues = () => {
        if (!dimensions || !showLabels) return;

        // Extract values from dimensions
        const { size, center, positions } = dimensions;

        const hasDimensionsChanged =
            previousDimensionsRef.current !== dimensions;

        if (!hasDimensionsChanged) return; // No updates needed if nothing has changed

        // Update previous values
        previousDimensionsRef.current = dimensions;

        const aspectRatio = maxAxisIndex(box) === 0 ? minAxisIndex(box) === 1 ? size.x / size.z : size.x / size.z : 
        maxAxisIndex(box) === 1 ? minAxisIndex(box) === 0 ? size.y / size.x : size.y / size.z : minAxisIndex(box) === 0 ? 
        size.z / size.x : size.z / size.y;
        const asjustedMinLabelOffset = unit === "mm" ? minLabelOffset * 0.001 : unit === "cm" ? minLabelOffset * 0.01 : minLabelOffset;

        const adjustedLabelOffset = labelOffset * (size.x + size.z) / 2;
        const adjustedLabelOffsetX = Math.max(asjustedMinLabelOffset, adjustedLabelOffset * aspectRatio / 2);
        const adjustedLabelOffsetZ = Math.max(asjustedMinLabelOffset, adjustedLabelOffset * aspectRatio / 2);

        log('adjusted label offset, adjustedLabelOffsetX, positions.backBottomLeft3.x, (positions.backBottomLeft3.x) + Math.sign(box.min.x) * adjustedLabelOffsetX:', 
            adjustedLabelOffset, adjustedLabelOffsetX, positions.backBottomLeft3.x,
        (positions.backBottomLeft3.x) + Math.sign(box.min.x) * adjustedLabelOffsetX);

        log('aspect ratio:', aspectRatio);

        const vector1 = new THREE.Vector3(
            0,
            dimensionsOnTop ? box.max.y - box.min.y : 0,
            0
        );
        const vector2 = positions.backBottomLeft2;

        const result = vector1.clone().add(vector2);

        function getPosition(position, defaultValue = new THREE.Vector3(0, 0, 0)) {
            return position ?? defaultValue;
        }

        log('dimensions map ref:', dimensionsMapRef.current);

        // Parse productSettings to check for Screw Length
        const productSettings = typeof configJson === "string" ? JSON.parse(configJson) : configJson;
        const screwLength = productSettings?.["Screw Length"];
        const screwLengthValue = screwLength != null && screwLength !== "" ? parseFloat(screwLength) : null;
        const isValidScrewLength = screwLengthValue != null && !isNaN(screwLengthValue) && screwLengthValue > 0;

        const derivedValues = {
            depthLabel: `${(size.z * scaleFactor).toFixed(numDigits) + unit}`,
            smallDimDepthLabel: `Ø  ${(size.z * scaleFactor).toFixed(numDigits) + unit}`,
            depthStart: new THREE.Vector3(0, dimensionsOnTop ? (box.max.y - box.min.y) : 0, 0).clone().add(positions.backBottomLeft2),
            depthEnd: new THREE.Vector3(0, dimensionsOnTop ? (box.max.y - box.min.y) : 0, 0).clone().add(positions.frontBottomLeft2),
            depthOffset: new THREE.Vector3(0, dimensionsOnTop ? (box.max.y - box.min.y) : 0, 0).clone().add(
                new THREE.Vector3(
                    box.min.x - line_model_offset,
                    box.min.y + line_floor_offset,
                    (box.max.z + box.min.z) / 2)
            ),
            smallDimDepthStart: new THREE.Vector3(box.max.x / 2, box.max.y - line_floor_offset, box.max.z / 2),
            smallDimDepthEnd: new THREE.Vector3(0, box.max.y, 0).clone().add(
                new THREE.Vector3(
                    3 * box.max.x,
                    - 2 * line_floor_offset,
                    3 * box.max.z)
            ),
            smallDimDepthOffset: new THREE.Vector3(0, box.max.y, 0).clone().add(
                new THREE.Vector3(
                    3 * box.max.x,
                    - 2 * line_floor_offset,
                    3 * box.max.z)
            ),
            heightHeadLabel: `${dimensionsMapRef.current.get('height_head') ?
                (dimensionsMapRef.current.get('height_head').value).toFixed(numDigits) : 0} ${unit}`,
            heightHeadStart: getPosition(positions.backBottomLeftHead),
            heightHeadEnd: getPosition(positions.topLeftHead),
            //Work out how to implement offset
            heightHeadOffset: new THREE.Vector3(
                getPosition(positions.backBottomLeftHead).x,
                (getPosition(positions.backBottomLeftHead).y + getPosition(positions.topLeftHead).y) / 2,
                getPosition(positions.backBottomLeftHead).z
            )
            ,
            lengthShaftLabel: `${dimensionsMapRef.current.get('length_shaft') ?
                (dimensionsMapRef.current.get('length_shaft').value) : 0} ${unit}`,
            lengthShaftStart: getPosition(positions.backBottomLeftShaft),
            lengthShaftEnd: getPosition(positions.topLeftShaft),
            //Work out how to implement offset
            lengthShaftOffset: new THREE.Vector3(
                getPosition(positions.backBottomLeftShaft).x,
                (getPosition(positions.backBottomLeftShaft).y + getPosition(positions.topLeftShaft).y) / 2,
                getPosition(positions.backBottomLeftShaft).z
            )
            ,
            lengthThreadLabel: `${dimensionsMapRef.current.get('length') ?
                (dimensionsMapRef.current.get('length').value) : 0}`,
            lengthThreadStart: getPosition(positions.backBottomLeftThread),
            lengthThreadEnd: getPosition(positions.topLeftThread),
            lengthThreadOffset: new THREE.Vector3(
                getPosition(positions.backBottomLeftThread).x + Math.sign(box.min.x) * adjustedLabelOffsetX,
                ((getPosition(positions.backBottomLeftThread).y + getPosition(positions.topLeftThread).y) / 2),
                getPosition(positions.backBottomLeftThread).z + Math.sign(box.min.z) * adjustedLabelOffsetZ
            )
            ,
            diameterShaftLabel: `${dimensionsMapRef.current.get('gauge') ?
                (dimensionsMapRef.current.get('gauge').value) : 0}`,
            //Need an offset on the x/z axis
            diameterShaftStart: getPosition(positions.middleDiameter),
            diameterShaftArrowStart: new THREE.Vector3(0, 0, -arrow_head_offset).clone().add(
                getPosition(positions.backMiddleBottomDiameter)),
            diameterShaftEnd: getPosition(positions.frontMiddleBottomDiameter),
            diameterShaftArrowEnd: new THREE.Vector3(0, 0, arrow_head_offset).clone().add(
                getPosition(positions.frontMiddleBottomDiameter)),
            diameterShaftOffset: new THREE.Vector3(
                positions.frontMiddleBottomDiameter.x + Math.sign(box.max.x) * adjustedLabelOffsetX / 2,
                positions.frontMiddleBottomDiameter.y,
                positions.frontMiddleBottomDiameter.z + Math.sign(box.max.z) * adjustedLabelOffsetZ / 2
            ),
            widthLabel: `${(size.x * scaleFactor).toFixed(numDigits)} ${unit}`,
            widthStart: new THREE.Vector3(0, dimensionsOnTop ? (box.max.y - box.min.y) : 0, 0).clone().add(positions.backBottomRight1),
            widthEnd: new THREE.Vector3(0, dimensionsOnTop ? (box.max.y - box.min.y) : 0, 0).clone().add(positions.backBottomLeft1),
            widthOffset: new THREE.Vector3(0, dimensionsOnTop ? (box.max.y - box.min.y) : 0, 0).clone().add(
                new THREE.Vector3(
                    (box.max.x + box.min.x) / 2,
                    box.min.y + line_floor_offset,
                    box.min.z - line_model_offset)
            ),
            heightLabel: isValidScrewLength ? `${screwLengthValue.toFixed(numDigits)} ${unit}` : `${(size.y * scaleFactor).toFixed(numDigits)} ${unit}`,
            heightStart: positions.backBottomLeft3,
            heightEnd: positions.topLeft,
            heightOffset: new THREE.Vector3(
                (positions.backBottomLeft3.x) + Math.sign(box.min.x) * adjustedLabelOffsetX,
                (box.max.y + box.min.y) / 2,
                positions.backBottomLeft3.z + Math.sign(box.min.z) * adjustedLabelOffsetZ
            )

        };

        log('depthLabel:', derivedValues.depthLabel); 
        // log('depthStart:', derivedValues.depthStart);
        // log('depthEnd:', derivedValues.depthEnd);
        log('widthLabel:', derivedValues.widthLabel); 
        // log('widthStart:', derivedValues.widthStart);
        // log('widthEnd:', derivedValues.widthEnd);
        log('lengthShaftLabel:', derivedValues.lengthShaftLabel); 
        // log('lengthShaftStart:', derivedValues.lengthShaftStart);
        // log('lengthShaftEnd:', derivedValues.lengthShaftEnd);
        log('heightLabel:', derivedValues.heightLabel); 
        // log('heightStart:', derivedValues.heightStart);
        // log('heightEnd:', derivedValues.heightEnd);
        log('diameterShaftLabel:', derivedValues.diameterShaftLabel);
        log('diameterShaftOffset:', derivedValues.diameterShaftOffset);
        log('diameterShaftStart:', derivedValues.diameterShaftStart);
        log('diameterShaftEnd:', derivedValues.diameterShaftEnd);

        log('heightHeadLabel:', derivedValues.heightHeadLabel);
        log('lengthThreadLabel, start, end:', derivedValues.lengthThreadLabel, lengthThreadStart, lengthThreadEnd);
        log('lengthThreadStart, lengthThreadEnd')
        //We're getting 0, 0, 0 for both start and end


        // Update states for labels and positions
        //setDepthLabel(derivedValues.depthLabel);
        //setDepthStart(derivedValues.depthStart);
        //setDepthEnd(derivedValues.depthEnd);
        //setDepthOffset(derivedValues.depthOffset);
        //setSmallDimDepthLabel(derivedValues.smallDimDepthLabel);
        //setSmallDimDepthStart(derivedValues.smallDimDepthStart);
        //setSmallDimDepthEnd(derivedValues.smallDimDepthEnd);
        //setSmallDimDepthOffset(derivedValues.smallDimDepthOffset);

        //setWidthLabel(derivedValues.widthLabel);
        //setWidthStart(derivedValues.widthStart);
        //setWidthEnd(derivedValues.widthEnd);
        //setWidthOffset(derivedValues.widthOffset);

        setHeightLabel(derivedValues.heightLabel);
        setHeightStart(derivedValues.heightStart);
        setHeightEnd(derivedValues.heightEnd);
        setHeightOffset(derivedValues.heightOffset);

        // setHeightHeadLabel(derivedValues.heightHeadLabel);
        // setHeightHeadStart(derivedValues.heightHeadStart);
        // setHeightHeadEnd(derivedValues.heightHeadEnd);
        // setHeightHeadOffset(derivedValues.heightHeadOffset);

        //setLengthShaftLabel(derivedValues.lengthShaftLabel);
        //setLengthShaftStart(derivedValues.lengthShaftStart);
        //setLengthShaftEnd(derivedValues.lengthShaftEnd);
        //setLengthShaftOffset(derivedValues.lengthShaftOffset);
        //log('shaft start, end:', derivedValues.lengthShaftStart, derivedValues.lengthShaftEnd);

        log('dimensions map:', dimensionsMapRef.current);
        log('length thread label:', derivedValues.lengthThreadLabel);
        setLengthThreadLabel(derivedValues.lengthThreadLabel);
        setLengthThreadStart(derivedValues.lengthThreadStart);
        setLengthThreadEnd(derivedValues.lengthThreadEnd);
        setLengthThreadOffset(derivedValues.lengthThreadOffset);

        setDiameterShaftLabel(derivedValues.diameterShaftLabel);
        setDiameterShaftStart(derivedValues.diameterShaftStart);
        setDiameterShaftEnd(derivedValues.diameterShaftEnd);
        // setDiameterShaftArrowStart(derivedValues.diameterShaftArrowStart);
        // setDiameterShaftArrowEnd(derivedValues.diameterShaftArrowEnd);
        setDiameterShaftOffset(derivedValues.diameterShaftOffset);
    };

    // Update camera direction in useFrame only
    useFrame(() => {
        if (!showLabels) return;
        updateValues();
    });

    return (
        <>
            {!isCameraSettled ? null :
                (<>
                {/* An invalid depthLabel is still processed */}
                    {depthLabel && parseFloat(depthLabel) !== 0 ?
                        (
                            <>
                                <DimensionLine params={params} line_width={line_width} start={depthStart} end={depthEnd} 
                                visible={showLabels} lineOffset={lineOffset} scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor} />
                                <DimensionLabel params={params} position={depthOffset} label={depthLabel} visible={showLabels} isMobile={isMobile}
                                    inverse={sceneBackgroundColour === 'black'} />
                                <Arrowhead params={params} position={depthStart} direction={depthEnd} visible={showLabels}
                                    scaleFactor={scaleFactor}
                                    distanceScale={distanceScale}
                                    lineOffset={lineOffset}
                                    setScaledArrowHeightOut={setScaledArrowHeightOut}
                                    minArrowHeight={minArrowHeight}
                                />
                                <Arrowhead params={params} position={depthEnd} direction={depthStart} visible={showLabels}
                                    scaleFactor={scaleFactor}
                                    distanceScale={distanceScale}
                                    lineOffset={lineOffset}
                                    setScaledArrowHeightOut={setScaledArrowHeightOut}
                                    minArrowHeight={minArrowHeight}
                                />
                            </>
                        )
                        : smallDimDepthLabel && parseFloat(smallDimDepthLabel) !== 0 ? (
                            <>
                                <DimensionLine params={params} line_width={line_width} start={smallDimDepthStart} end={smallDimDepthEnd} 
                                visible={showLabels} lineOffset={lineOffset} scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor}/>
                                <DimensionLabel params={params} position={smallDimDepthOffset} label={smallDimDepthLabel} visible={showLabels} isMobile={isMobile}
                                    inverse={sceneBackgroundColour === 'black'} />
                            </>
                        ) : null
                    }
                    {widthLabel && parseFloat(widthLabel) !== 0 ?
                        (
                            <>
                                <DimensionLine params={params} line_width={line_width} start={widthStart} end={widthEnd} visible={showLabels} 
                                lineOffset={lineOffset} scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor}/>
                                <DimensionLabel params={params} position={widthOffset} label={widthLabel} visible={showLabels} isMobile={isMobile}
                                    inverse={sceneBackgroundColour === 'black'} />
                                <Arrowhead params={params} position={widthStart} direction={widthEnd} visible={showLabels}
                                    scaleFactor={scaleFactor}
                                    distanceScale={distanceScale}
                                    lineOffset={lineOffset}
                                    setScaledArrowHeightOut={setScaledArrowHeightOut}
                                    minArrowHeight={minArrowHeight}
                                />
                                <Arrowhead params={params} position={widthEnd} direction={widthStart} visible={showLabels}
                                    scaleFactor={scaleFactor}
                                    distanceScale={distanceScale}
                                    lineOffset={lineOffset}
                                    setScaledArrowHeightOut={setScaledArrowHeightOut}
                                    minArrowHeight={minArrowHeight}
                                />
                            </>)
                        : (null)
                    }

                    <DimensionLine params={params} line_width={line_width} start={new THREE.Vector3(0, heightStart.y, 0)} end={heightStart} lineOffset={lineOffset}
                    visible={showLabels} lineStyle='dotted' scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor} />            

                    {fullLengthDimension ?
                        <>
                            (<DimensionLine params={params} line_width={line_width} start={heightStart} end={heightEnd} visible={showLabels} 
                            lineOffset={lineOffset} scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor} />
                            )
                        </> : null
                     }
                    {fullLengthDimension && heightLabel && parseFloat(heightLabel) !== 0 ?
                        <DimensionLabel params={params} position={heightOffset} label={heightLabel} visible={showLabels} isMobile={isMobile}
                            inverse={sceneBackgroundColour === 'black'} /> : null}

                    {fullLengthDimension ?
                    (
                        <>
                            <DimensionLine params={params} line_width={line_width} start={new THREE.Vector3(0, heightEnd.y, 0)} end={heightEnd}
                            visible={showLabels} lineStyle='dotted' lineOffset={lineOffset} scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor}/>

                            <Arrowhead
                                params={params}
                                position={heightStart}
                                direction={heightEnd}
                                visible={showLabels}
                                scaleFactor={scaleFactor}
                                distanceScale={distanceScale}
                                lineOffset={lineOffset}
                                setScaledArrowHeightOut={setScaledArrowHeightOut}
                                minArrowHeight={minArrowHeight}
                            />
                            <Arrowhead
                                params={params}
                                position={heightEnd}
                                direction={heightStart}
                                visible={showLabels}
                                scaleFactor={scaleFactor}
                                distanceScale={distanceScale}
                                lineOffset={lineOffset}
                                setScaledArrowHeightOut={setScaledArrowHeightOut}
                                minArrowHeight={minArrowHeight}
                            />
                        </>
                    ) : null}
                    

                    {(heightHeadLabel && parseFloat(heightHeadLabel) !== 0) ?
                        (<>
                            <DimensionLine params={params} line_width={line_width} start={heightHeadStart} end={heightHeadEnd} 
                            visible={showLabels} lineOffset={lineOffset} scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor} />
                            <DimensionLabel params={params} position={heightHeadOffset} label={heightHeadLabel} visible={showLabels} isMobile={isMobile}
                                inverse={sceneBackgroundColour === 'black'} />
                            <DimensionLine params={params} line_width={line_width} start={new THREE.Vector3(0, heightHeadStart.y, 0)} end={heightHeadStart} lineOffset={lineOffset}
                                visible={showLabels} lineStyle='dotted' scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor} />
                            <Arrowhead params={params} position={heightHeadStart} direction={heightHeadEnd} visible={showLabels} flip={true}
                                scaleFactor={scaleFactor}
                                distanceScale={distanceScale}
                                lineOffset={lineOffset}
                                setScaledArrowHeightOut={setScaledArrowHeightOut}
                                minArrowHeight={minArrowHeight}
                            />
                            <Arrowhead params={params} position={heightHeadEnd} direction={heightHeadStart} visible={showLabels} flip={true}
                                scaleFactor={scaleFactor}
                                distanceScale={distanceScale}
                                lineOffset={lineOffset}
                                setScaledArrowHeightOut={setScaledArrowHeightOut}
                                minArrowHeight={minArrowHeight}
                            />
                        </>)
                        : (null)}

                    {(lengthShaftLabel && parseFloat(lengthShaftLabel) !== 0) ?
                        (<>
                            <DimensionLine params={params} line_width={line_width} start={lengthShaftStart} end={lengthShaftEnd} 
                            visible={showLabels} lineOffset={lineOffset} scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor} />
                            <DimensionLabel params={params} position={lengthShaftOffset} label={lengthShaftLabel} visible={showLabels} isMobile={isMobile}
                                inverse={sceneBackgroundColour === 'black'} />
                            <DimensionLine params={params} line_width={line_width} start={new THREE.Vector3(0, lengthShaftEnd.y, 0)} end={lengthShaftEnd} lineOffset={lineOffset}
                                visible={showLabels} lineStyle='dotted' scaledArrowHeight={scaledArrowHeight} scaleFactor={scaleFactor} />
                            <Arrowhead params={params} position={lengthShaftStart} direction={lengthShaftEnd} visible={showLabels}
                                scaleFactor={scaleFactor}
                                distanceScale={distanceScale}
                                lineOffset={lineOffset}
                                setScaledArrowHeightOut={setScaledArrowHeightOut}
                                minArrowHeight={minArrowHeight}
                            />
                            <Arrowhead params={params} position={lengthShaftEnd} direction={lengthShaftStart} visible={showLabels}
                                scaleFactor={scaleFactor}
                                distanceScale={distanceScale}
                                lineOffset={lineOffset}
                                setScaledArrowHeightOut={setScaledArrowHeightOut}
                                minArrowHeight={minArrowHeight}
                            />
                        </>)
                        : (null)}

                    {(!fullLengthDimension && lengthThreadLabel) ?
                        (
                            <>
                                <DimensionLine params={params} line_width={line_width} start={lengthThreadStart} end={lengthThreadEnd} 
                                visible={showLabels} lineOffset={lineOffset} scaledArrowHeight={scaledArrowHeight} 
                                scaleFactor={scaleFactor} />
                                <DimensionLabel params={params} position={lengthThreadOffset} label={lengthThreadLabel} visible={showLabels} isMobile={isMobile} 
                                    inverse={sceneBackgroundColour === 'black'} />
                                <DimensionLine params={params} line_width={line_width} start={new THREE.Vector3(0, lengthThreadEnd.y, 0)} 
                                end={lengthThreadEnd} lineOffset={lineOffset} visible={showLabels} lineStyle='dotted' scaledArrowHeight={scaledArrowHeight} 
                                scaleFactor={scaleFactor}/>
                                <Arrowhead 
                                    params={params} 
                                    position={lengthThreadStart} 
                                    direction={lengthThreadEnd} 
                                    visible={showLabels}
                                    scaleFactor={scaleFactor}
                                    distanceScale={distanceScale}
                                    lineOffset={lineOffset}
                                    setScaledArrowHeightOut={setScaledArrowHeightOut}
                                    minArrowHeight={minArrowHeight}
                                />
                                <Arrowhead 
                                    params={params} 
                                    position={lengthThreadEnd} 
                                    direction={lengthThreadStart} 
                                    visible={showLabels}
                                    scaleFactor={scaleFactor}
                                    distanceScale={distanceScale}
                                    lineOffset={lineOffset}
                                    setScaledArrowHeightOut={setScaledArrowHeightOut}
                                    minArrowHeight={minArrowHeight}
                                />
                            </>
                        )
                        : (null)}

                    {(diameterShaftLabel && parseFloat(diameterShaftLabel) !== 0) ?
                        (
                            <>
                                <DimensionLine params={params} line_width={line_width} start={diameterShaftStart} end={diameterShaftEnd} 
                                visible={showLabels} lineStyle='dotted' lineOffset={lineOffset} scaleFactor={scaleFactor} />
                                {/* <DimensionLine params={params} line_width={line_width} start={new THREE.Vector3(0, lengthThreadEnd.y, 0)} end={new THREE.Vector3(0.005, lengthThreadEnd.y, 0)} visible={showLabels} /> */}
                                <DimensionLabel params={params} position={diameterShaftOffset} label={diameterShaftLabel} visible={showLabels} isMobile={isMobile}
                                    inverse={sceneBackgroundColour === 'black'} />
                            </>
                        )
                        : (null)}
                </>)
            }

        </>
    );
};

export default SimpleCoordinates;